'use client'

import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useScroll } from 'motion/react'
import { usePathname } from 'next/navigation'

import { ZONES } from '~/config/zones'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useAuth } from '~/providers/auth-provider'
import { BrandLink } from './brand-link'
import { DesktopNav } from './desktop-nav'
import { GuestMenu } from './guest-menu'
import { MobileNav } from './mobile-nav'

const NavbarSkeleton = () => (
	<div className='flex flex-row items-center gap-8'>
		<Skeleton className='h-[16px] w-[106px] max-md:hidden' />
		<Skeleton className='h-[20px] w-[20px] max-md:hidden' />
		<Skeleton className='h-[40px] w-[80px] rounded-full max-md:hidden' />
	</div>
)

const KNOWN_TOP_LEVEL_ROUTES = new Set([
	'',
	'account',
	'auth',
	'blog',
	'booking',
	'bureau-a-l-heure',
	'dashboard',
	'events',
	'favorites',
	'london',
	'lyon',
	'mentions-legales',
	'offers',
	'on-site-qr',
	'overstays',
	'paris',
	'payment',
	'politique-confidentialite',
	'quotations',
	'reglement-cocoon',
	'salle-de-reunion',
	'scheduling',
	'unsubscribe',
	'use-cases',
	'venues',
])

const isUserPublicPage = (pathname: string) => {
	const segments = pathname.split('/').filter(Boolean)

	if (segments.length === 1) {
		return !KNOWN_TOP_LEVEL_ROUTES.has(segments[0])
	}

	if (segments.length === 2) {
		return !KNOWN_TOP_LEVEL_ROUTES.has(segments[0])
	}

	return false
}

const useHomeScrollDetection = () => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { scrollY } = useScroll()

	const [showTransparentBar, setShowTransparentBar] = useState(false)

	useEffect(() => {
		setShowTransparentBar(true)

		const unsub = scrollY.on('change', (y) => {
			return setShowTransparentBar(y < 450)
		})

		return () => unsub()
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [])

	return useMemo(() => {
		if (pathname !== '/') return false
		return showTransparentBar
	}, [pathname, showTransparentBar])
}

const AppMenu = () => {
	const isMobile = useIsMobile()
	const { isAuth, isLoading } = useAuth()

	if (isLoading) return <NavbarSkeleton />
	if (!isAuth) return <GuestMenu />
	return isMobile ? <MobileNav /> : <DesktopNav />
}

const HIDDEN_ON_MOBILE_PATTERNS = [
	/^\/booking\/[^/]+\/payment$/,
	/^\/events\/[^/]+$/,
	...ZONES.map((zone) => new RegExp(`^/${zone}/[^/]+$`)),
]

export const AppNavbar = ({
	className,
}: PropsWithChildren<{ className?: string }>) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const isMobile = useIsMobile()
	const { isAuth } = useAuth()
	const showTransparentBar = useHomeScrollDetection()

	const isHidden = useMemo(() => {
		return !isAuth && isUserPublicPage(pathname)
	}, [pathname, isAuth])

	const isHiddenOnMobile = useMemo(
		() => HIDDEN_ON_MOBILE_PATTERNS.some((pattern) => pattern.test(pathname)),
		[pathname],
	)

	if ((isMobile && isHiddenOnMobile) || isHidden) return null

	return (
		<nav
			className={cn(
				'flex h-[60px] w-full shrink-0 items-center justify-between bg-white px-4 shadow transition-all delay-100 print:hidden',
				'fixed inset-x-0 bottom-0 z-40 md:sticky md:top-0 md:bottom-auto',
				{
					'sticky top-0 bottom-auto': !isAuth,
					'!fixed !bg-transparent shadow-none [&_a]:!text-white [&_button]:!text-white':
						showTransparentBar,
				},
				className,
			)}
		>
			<BrandLink />
			<AppMenu />
		</nav>
	)
}
